import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, Search } from 'lucide-react';
import { apiService } from '../../services/api';

interface ToolInfo {
  name: string;
  description: string;
  category: string;
  source: string;
}

interface ToolCategory {
  name: string;
  display_name: string;
  tools: ToolInfo[];
}

interface AvailableToolsResponse {
  categories: ToolCategory[];
  total_tools: number;
}

interface ToolSelectorProps {
  selectedTools: string[] | null; // null = all tools
  onChange: (tools: string[] | null) => void;
  processorType: 'agentic_task' | 'orchestrator' | 'vector';
}

const ToolSelector: React.FC<ToolSelectorProps> = ({
  selectedTools,
  onChange,
  processorType,
}) => {
  const [availableTools, setAvailableTools] = useState<AvailableToolsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set()); // 'builtin' | 'mcp'

  // Load available tools on mount
  useEffect(() => {
    const fetchTools = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await apiService['client'].get<AvailableToolsResponse>('/tools');
        setAvailableTools(response.data);
        
        // Don't auto-expand anything - all sections/categories start collapsed
      } catch (err) {
        console.error('Failed to fetch tools:', err);
        setError('Failed to load available tools');
      } finally {
        setLoading(false);
      }
    };

    fetchTools();
  }, []);

  const handleCategoryToggle = (categoryName: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(categoryName)) {
        next.delete(categoryName);
      } else {
        next.add(categoryName);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    onChange(null); // null means all tools
  };

  const handleDeselectAll = () => {
    onChange([]); // empty array means no tools
  };

  const handleToggleTool = (toolName: string) => {
    if (selectedTools === null) {
      // Currently "all tools" - switch to explicit selection with everything except this tool
      const allToolNames = availableTools!.categories.flatMap(cat =>
        cat.tools.map(t => t.name)
      );
      onChange(allToolNames.filter(name => name !== toolName));
    } else {
      // Toggle in explicit selection
      if (selectedTools.includes(toolName)) {
        onChange(selectedTools.filter(name => name !== toolName));
      } else {
        onChange([...selectedTools, toolName]);
      }
    }
  };

  const handleToggleCategory = (category: ToolCategory) => {
    const categoryToolNames = category.tools.map(t => t.name);
    
    if (selectedTools === null) {
      // Currently "all tools" - switch to explicit selection without this category
      const allToolNames = availableTools!.categories.flatMap(cat =>
        cat.tools.map(t => t.name)
      );
      onChange(allToolNames.filter(name => !categoryToolNames.includes(name)));
    } else {
      // Check if all tools in category are selected
      const allSelected = categoryToolNames.every(name => selectedTools.includes(name));
      
      if (allSelected) {
        // Deselect all in category
        onChange(selectedTools.filter(name => !categoryToolNames.includes(name)));
      } else {
        // Select all in category
        const newSelection = new Set([...selectedTools, ...categoryToolNames]);
        onChange(Array.from(newSelection));
      }
    }
  };

  const isToolSelected = (toolName: string): boolean => {
    return selectedTools === null || selectedTools.includes(toolName);
  };

  const isCategorySelected = (category: ToolCategory): boolean => {
    if (selectedTools === null) return true;
    return category.tools.every(tool => selectedTools.includes(tool.name));
  };

  const isCategoryPartiallySelected = (category: ToolCategory): boolean => {
    if (selectedTools === null) return false;
    const selectedCount = category.tools.filter(tool => 
      selectedTools.includes(tool.name)
    ).length;
    return selectedCount > 0 && selectedCount < category.tools.length;
  };

  const getCategorySelectedCount = (category: ToolCategory): number => {
    if (selectedTools === null) return category.tools.length;
    return category.tools.filter(tool => selectedTools.includes(tool.name)).length;
  };

  const handleSectionToggle = (sectionName: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(sectionName)) {
        next.delete(sectionName);
      } else {
        next.add(sectionName);
      }
      return next;
    });
  };

  const getFilteredCategories = (): ToolCategory[] => {
    if (!availableTools || !searchTerm) return availableTools?.categories || [];

    const lowerSearch = searchTerm.toLowerCase();
    return availableTools.categories
      .map(category => ({
        ...category,
        tools: category.tools.filter(
          tool =>
            tool.name.toLowerCase().includes(lowerSearch) ||
            tool.description.toLowerCase().includes(lowerSearch)
        ),
      }))
      .filter(category => category.tools.length > 0);
  };

  const isMcpCategory = (category: ToolCategory): boolean => {
    // A category is MCP if any of its tools have a source starting with "mcp-"
    return category.tools.some(tool => tool.source.startsWith('mcp-'));
  };

  const getBuiltinCategories = (): ToolCategory[] => {
    const filtered = getFilteredCategories();
    return filtered.filter(cat => !isMcpCategory(cat));
  };

  const getMcpCategories = (): ToolCategory[] => {
    const filtered = getFilteredCategories();
    return filtered.filter(cat => isMcpCategory(cat));
  };

  const getSelectedToolsInCategories = (categories: ToolCategory[]): number => {
    if (selectedTools === null) {
      // All tools selected
      return categories.reduce((sum, cat) => sum + cat.tools.length, 0);
    }
    return categories.reduce((sum, cat) => {
      return sum + cat.tools.filter(tool => selectedTools.includes(tool.name)).length;
    }, 0);
  };

  const getSelectionSummary = (): string => {
    if (selectedTools === null) {
      return `All tools (${availableTools?.total_tools || 0})`;
    }
    return `${selectedTools.length} of ${availableTools?.total_tools || 0} tools`;
  };

  if (loading) {
    return (
      <div className="p-4 text-center text-gray-500 dark:text-gray-400">
        Loading available tools...
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded p-3 text-red-700 dark:text-red-300">
          {error}
        </div>
      </div>
    );
  }

  if (!availableTools) {
    return null;
  }

  const builtinCategories = getBuiltinCategories();
  const mcpCategories = getMcpCategories();
  const hasBuiltin = builtinCategories.length > 0;
  const hasMcp = mcpCategories.length > 0;

  // Helper to render a section of categories
  const renderSection = (
    sectionKey: string,
    sectionTitle: string,
    categories: ToolCategory[]
  ) => {
    if (categories.length === 0) return null;

    const isSectionExpanded = expandedSections.has(sectionKey);
    const selectedInSection = getSelectedToolsInCategories(categories);

    return (
      <div className="border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
        {/* Section Header */}
        <button
          onClick={() => handleSectionToggle(sectionKey)}
          className="w-full flex items-center justify-between p-3 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700"
        >
          <div className="flex items-center gap-2">
            {isSectionExpanded ? (
              <ChevronDown className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            ) : (
              <ChevronRight className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            )}
            <span className="font-semibold text-sm text-gray-900 dark:text-white">
              {sectionTitle}
            </span>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Show selected count when collapsed and tools are selected */}
            {!isSectionExpanded && selectedInSection > 0 && (
              <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded text-xs font-medium">
                {selectedInSection} {selectedInSection === 1 ? 'tool' : 'tools'} selected
              </span>
            )}
            <span className="px-2 py-0.5 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded text-xs">
              {categories.length} {categories.length === 1 ? 'category' : 'categories'}
            </span>
          </div>
        </button>

        {/* Section Content */}
        {isSectionExpanded && (
          <div className="p-3 space-y-2 bg-white dark:bg-gray-900">
            {categories.map(category => {
              const isExpanded = expandedCategories.has(category.name);
              const isSelected = isCategorySelected(category);
              const isPartial = isCategoryPartiallySelected(category);
              const selectedCount = getCategorySelectedCount(category);
              const totalCount = category.tools.length;

              return (
                <div
                  key={category.name}
                  className="border border-gray-200 dark:border-gray-700 rounded overflow-hidden"
                >
                  {/* Category Header */}
                  <div className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center p-2 gap-2">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        ref={(input) => {
                          if (input) {
                            input.indeterminate = isPartial;
                          }
                        }}
                        onChange={() => handleToggleCategory(category)}
                        className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 
                          text-blue-600 focus:ring-2 focus:ring-blue-500 cursor-pointer"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <button
                        onClick={() => handleCategoryToggle(category.name)}
                        className="flex-1 flex items-center justify-between text-left"
                      >
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {category.display_name}
                        </span>
                        <div className="flex items-center gap-2">
                          <span className="px-2 py-0.5 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded text-xs">
                            {selectedCount} of {totalCount}
                          </span>
                          {isExpanded ? (
                            <ChevronDown className="w-4 h-4 text-gray-500" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-gray-500" />
                          )}
                        </div>
                      </button>
                    </div>
                  </div>

                  {/* Category Tools */}
                  {isExpanded && (
                    <div className="p-2 space-y-1">
                      {category.tools.map(tool => (
                        <label
                          key={tool.name}
                          className="flex items-start gap-2 p-2 rounded hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={isToolSelected(tool.name)}
                            onChange={() => handleToggleTool(tool.name)}
                            className="mt-0.5 w-4 h-4 rounded border-gray-300 dark:border-gray-600 
                              text-blue-600 focus:ring-2 focus:ring-blue-500 cursor-pointer"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {tool.name}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                              {tool.description}
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="w-full space-y-4">
      {/* Header with selection summary */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`px-2 py-1 rounded text-xs font-medium ${
          selectedTools === null
            ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300'
            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
        }`}>
          {getSelectionSummary()}
        </span>
        <button
          onClick={handleSelectAll}
          className="px-3 py-1 text-xs font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded"
        >
          Select All
        </button>
        <button
          onClick={handleDeselectAll}
          className="px-3 py-1 text-xs font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded"
        >
          Deselect All
        </button>
      </div>

      {/* Search bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          placeholder="Search tools..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded
            bg-white dark:bg-gray-800 text-gray-900 dark:text-white
            focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
        />
      </div>

      {/* Tool sections */}
      {!hasBuiltin && !hasMcp ? (
        <p className="text-center py-8 text-gray-500 dark:text-gray-400">
          No tools match your search.
        </p>
      ) : (
        <div className="space-y-3">
          {/* Built-in Tools Section */}
          {renderSection('builtin', 'Built-in Tools', builtinCategories)}
          
          {/* MCP Tools Section */}
          {renderSection('mcp', 'MCP Tools', mcpCategories)}
        </div>
      )}
    </div>
  );
};

export default ToolSelector;
